import { CampaignPauseReason } from "@signalist/shared-types";

import { buildCampaignBuilderPath } from "@/pages/Private/pages/List/utils/campaignBuilderPath";
import { ROUTE_CONFIG } from "@/routes/config";

import { DashboardIssue, DashboardIssueCampaignPausedPayload } from "../schema/dashboard";

/** Structurally an `IssueDisplayProps`; kept apart so this module does not import its caller. */
export interface CampaignPausedIssueDisplay {
	title: string;
	description: string;
	primaryActionLabel: string;
	primaryActionTo: string;
}

interface PauseWording {
	/** Completes "… is paused: <headline>". */
	headline: string;
	description: string;
	/** Where the issue's button leads, when it is not the campaign's builder. */
	action?: { label: string; to: string };
}

/** Company Settings → Plans & Billing, the sidebar credits link's target. */
const BILLING_PATH = "/app/company?tab=BILLING";

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const numberOf = (detail: Record<string, unknown>, key: string): number | null => {
	const value = detail[key];

	return typeof value === "number" && Number.isFinite(value) ? value : null;
};

/**
 * What the dashboard says per pause reason (PREC-4059). Keyed by the shared enum, like the API's
 * mail copy, so a new reason does not compile until the dashboard can explain it too. Every
 * description ends with the step that clears the issue: re-activating the campaign, or adding
 * credits for an out-of-credits pause, which resumes the campaign by itself (PREC-2803).
 */
const WORDING: Record<CampaignPauseReason, (detail: Record<string, unknown>) => PauseWording> = {
	[CampaignPauseReason.INVITE_NOTE_TOO_LONG]: (detail) => {
		const actual = numberOf(detail, "actualLength");
		const limit = numberOf(detail, "limit");

		return {
			headline: "a connection note is too long",
			description:
				actual !== null && limit !== null
					? `A personalised connection note came to ${actual} characters, over LinkedIn's limit of ${limit}. Shorten the note, then re-activate the campaign.`
					: "A personalised connection note is longer than LinkedIn allows. Shorten the note, then re-activate the campaign.",
		};
	},
	[CampaignPauseReason.NO_ACTIVE_SENDERS]: () => ({
		headline: "no active sender",
		description:
			"None of its senders is active, so no lead could be scheduled. Add a sender or re-activate one, then re-activate the campaign.",
	}),
	[CampaignPauseReason.NO_SENDER_FOR_FIRST_STEP_CHANNEL]: (detail) => {
		const isEmail = detail.channel === "email";
		const channel = isEmail ? "email" : "LinkedIn";
		const sender = isEmail ? "mailbox" : "LinkedIn account";

		return {
			headline: `no active ${channel} sender`,
			description: `Its first step is sent by ${channel}, but none of its active senders is a ${sender}, so no lead could be scheduled. Activate a ${sender} in the campaign's senders, then re-activate the campaign.`,
		};
	},
	[CampaignPauseReason.OUT_OF_CREDITS]: () => ({
		headline: "out of credits",
		description:
			"An email address could not be looked up before an email step, because the workspace has too few credits left. Add credits under Plans & Billing; the campaign then starts again by itself.",
		action: { label: "Add credits", to: BILLING_PATH },
	}),
};

/** An API newer than this page may send a reason it does not know yet; it still gets a sentence. */
const UNKNOWN_REASON_WORDING: PauseWording = {
	headline: "a lead could not be sent",
	description: "Open the campaign to fix it, then re-activate the campaign.",
};

const isKnownReason = (reason: string): reason is CampaignPauseReason =>
	(Object.values(CampaignPauseReason) as string[]).includes(reason);

/** The payload `detectCampaignPausedIssues` sends, or null when the issue does not carry one. */
export const asCampaignPausedPayload = (
	issue: DashboardIssue
): DashboardIssueCampaignPausedPayload | null => {
	const p = issue.payload ?? {};

	if (
		typeof p.listId !== "number" ||
		typeof p.campaignUuid !== "string" ||
		typeof p.reason !== "string"
	) {
		return null;
	}

	return {
		listId: p.listId,
		listName: typeof p.listName === "string" && p.listName ? p.listName : `List ${p.listId}`,
		campaignUuid: p.campaignUuid,
		campaignName:
			typeof p.campaignName === "string" && p.campaignName.trim()
				? p.campaignName
				: "Untitled campaign",
		reason: p.reason,
		detail: isRecord(p.detail) ? p.detail : {},
	};
};

/**
 * A campaign that paused itself because a lead could not be sent. The action opens that
 * campaign's builder, where the problem is fixed and the campaign re-activated — which also
 * clears this issue, since the API only reports pauses whose campaign is still paused.
 */
export const campaignPausedIssueDisplay = (issue: DashboardIssue): CampaignPausedIssueDisplay => {
	const payload = asCampaignPausedPayload(issue);

	if (!payload) {
		return {
			title: "A campaign was paused",
			description: "A lead could not be sent. Open your lists to find the paused campaign.",
			primaryActionLabel: "Open lists",
			primaryActionTo: ROUTE_CONFIG.LISTS,
		};
	}

	const wording = isKnownReason(payload.reason)
		? WORDING[payload.reason](payload.detail)
		: UNKNOWN_REASON_WORDING;

	return {
		title: `"${payload.campaignName}" on "${payload.listName}" is paused: ${wording.headline}`,
		description: wording.description,
		primaryActionLabel: wording.action?.label ?? "Edit campaign",
		primaryActionTo:
			wording.action?.to ??
			buildCampaignBuilderPath(payload.listId, {
				name: payload.campaignName,
				campaignId: payload.campaignUuid,
			}),
	};
};
